// Industry-specific landing-page copy for CCCHAPS.
//
// All copy in this file is original to California Corporate Chaplains.
// It has been written to reflect the CCCHAPS service model:
//   • confidential, non-clinical chaplaincy
//   • 24/7 on-call chaplain support (standard, not tier-gated)
//   • CALLACHAP, CCCHAPS Care Portal, CCCHAPS On Demand as included baseline
//   • scheduled on-site, remote, or blended presence based on Care Team Package
//   • aggregate, non-identifying organizational reporting
//   • leadership consultation where appropriate
//   • CCCHAPS complements — does not replace — EAP, HR, mental-health,
//     medical, legal, or emergency services.
//
// Do not import or reuse copy from other chaplaincy providers. Do not add
// unsupported retention/turnover/productivity/nationwide claims.
export const INDUSTRIES = {
  "automotive-dealerships": {
    slug: "automotive-dealerships",
    eyebrow: "Industries We Serve",
    h1: "Workplace Chaplain Support for Automotive & Dealership Teams",
    lede:
      "Support the people behind the showroom, service drive, parts department, body shop, and back office with confidential chaplain care that meets employees where they are.",
    heroImage: {
      src: "/images/industries/automotive-dealerships/hero.jpg",
      alt: "A smiling automotive service employee in coveralls with a clipboard beside a vehicle in a dealership service bay.",
    },
    rolesImage: {
      src: "/images/industries/automotive-dealerships/service-ops.jpg",
      alt: "Two automotive service technicians working together on a vehicle inside a dealership service shop.",
    },
    meta: {
      title:
        "Workplace Chaplain Support for Automotive & Dealership Teams | California Corporate Chaplains",
      description:
        "Confidential, non-clinical workplace chaplain care for dealership sales, service, technicians, parts, administration, and leadership — 24/7 on-call chaplain access, CALLACHAP, Care Portal, and CCCHAPS On Demand included.",
    },
    whyItMatters: {
      title: "Why Chaplain Support Matters Here",
      body:
        "Dealership work is unusually public and unusually personal at the same time. A rough quarter, a hard customer, a family emergency at home — all of it lives in the same eight-to-twelve-hour day, often on a commission clock. CCCHAPS chaplains provide a steady, confidential person outside the reporting line who can listen without judgment, walk with employees through hard seasons, and simply be present when the day gets heavy.",
      challenges: [
        "High-pressure sales cycles and monthly targets that compound personal stress",
        "Customer-facing tension — from difficult purchases to service disputes",
        "Service and repair workload, technician time pressure, and shop dynamics",
        "Long, split, or irregular hours across weekend and holiday coverage",
        "Commission-driven income and the family stress that comes with it",
        "Family, relational, and caregiving concerns that follow employees into the workday",
        "Grief and serious illness — a coworker, a spouse, a parent, a customer's family",
        "Team changes, workplace pressure, and the human strain of a demanding retail environment",
      ],
    },
    howWeSupport: {
      title: "How CCCHAPS Supports Your Team",
      paragraphs: [
        "CCCHAPS chaplains work relationally, not transactionally. A chaplain becomes a familiar face on the drive, on the service walk-around, and in the back office — someone employees know before they need to reach out. Conversations are voluntary, confidential, and always the employee's choice.",
        "The organization designs its Care Team Package around workforce size, location count, department mix, and the level of on-site presence that fits the operation. Every arrangement includes 24/7 on-call chaplain access, CALLACHAP, the CCCHAPS Care Portal, and CCCHAPS On Demand so employees and their immediate household members always have a private way to reach a chaplain — whether during a floor shift, after close, or on a Sunday.",
        "CCCHAPS complements — it does not replace — your EAP, your HR team, medical or mental-health treatment, legal services, or emergency services. When a situation calls for one of those resources, the chaplain helps the employee connect and, with permission, walks alongside them through it.",
      ],
    },
    roles: {
      title: "Care Across the Entire Dealership",
      cards: [
        {
          label: "Sales & Finance",
          body:
            "Steady presence for consultants, F&I, and desk managers navigating quota pressure, long floor days, and the emotional weight that follows a difficult month.",
        },
        {
          label: "Service Advisors",
          body:
            "Confidential support for the people absorbing customer frustration all day — a private conversation that isn't a customer, a manager, or a coworker.",
        },
        {
          label: "Technicians",
          body:
            "Non-clinical care for the shop floor — grief, family stress, injury recovery, and the physical toll of production-paced work.",
        },
        {
          label: "Parts & Operations",
          body:
            "Support for parts advisors, drivers, and operations staff who keep the store moving but rarely get asked how they're doing.",
        },
        {
          label: "Administration",
          body:
            "Consistent, confidential presence for accounting, title, reception, and BDC teams — the roles that carry a lot and are often overlooked.",
        },
        {
          label: "Leadership",
          body:
            "Chaplain support and coordination for GMs, department heads, and owners — including consultation on team well-being and organizational care.",
        },
      ],
    },
  },

  "restaurants-hospitality": {
    slug: "restaurants-hospitality",
    eyebrow: "Industries We Serve",
    h1: "Workplace Chaplain Care for Restaurants, Fast-Food Franchises & Hospitality Teams",
    lede:
      "Confidential, relationship-based chaplain support for the people who serve others every day — across restaurants, quick-service and fast-food franchises, hotels, guest services, housekeeping, and hospitality operations.",
    heroImage: {
      src: "/images/industries/restaurants-hospitality/hero.jpg",
      alt: "Back-of-house restaurant staff working side-by-side at a kitchen counter in a warm, collaborative moment.",
    },
    rolesImage: {
      src: "/images/industries/restaurants-hospitality/housekeeping.jpg",
      alt: "A hotel housekeeping employee in uniform preparing a guest room, representing the broader hospitality workforce.",
    },
    meta: {
      title:
        "Workplace Chaplain Care for Restaurants, Fast-Food Franchises & Hospitality Teams | California Corporate Chaplains",
      description:
        "Confidential, non-clinical workplace chaplain care for restaurant, quick-service, and fast-food franchise teams — plus hotels, guest services, housekeeping, and hospitality leadership. 24/7 on-call chaplain access included.",
    },
    whyItMatters: {
      title: "Why Chaplain Support Matters Here",
      body:
        "Hospitality is emotional labor — and it looks the same whether it's a full-service dining room, a fast-food counter at 6 a.m., a housekeeping cart on the fourth floor, or a hotel front desk overnight. Teams work long hours to make everyone else's day better, and that adds up. CCCHAPS chaplains provide a private, non-clinical source of support employees can reach through the support channels available to their organization — during difficult seasons, after a hard shift, or when life and work collide.",
      challenges: [
        "Fast-paced service and back-to-back rushes — from full-service floors to drive-thru and quick-service peaks",
        "Irregular schedules, split shifts, and last-minute coverage across restaurants, franchises, and hotels",
        "Emotional strain from difficult guests, complaints, and public-facing conflict",
        "Staffing pressure and the weight of holding a shift short-handed",
        "Financial stress — tip-based income, hourly wages, seasonal swings, cost of living",
        "Family concerns that follow employees into a shift they can't leave",
        "Grief, loss, and personal crises during peak service periods",
        "Fatigue, emotional strain, and the cumulative pressure of a demanding hospitality environment",
        "Leadership pressure on GMs, chefs, franchise operators, and multi-site owners",
        "Culturally diverse teams that deserve respectful care across different backgrounds, beliefs, and life experiences",
      ],
    },
    howWeSupport: {
      title: "How CCCHAPS Supports Your Team",
      paragraphs: [
        "A CCCHAPS chaplain becomes a trusted face on the floor and in the back of house — not a manager, not clinical, not asking for anything. Chaplain visits are scheduled around service, coordinated with your leadership so care fits the rhythm of the operation rather than fighting it. That same rhythm is respected at a fast-food franchise counter, a hotel front desk, or a full-service dining room.",
        "Every Care Team Package includes 24/7 on-call chaplain access, CALLACHAP, the CCCHAPS Care Portal, and CCCHAPS On Demand — so a line cook after close, a shift lead at a quick-service location, a housekeeper at a hotel across town, or a franchise operator between locations can always reach a chaplain privately. Confidential support extends to employees and their immediate household members.",
        "CCCHAPS complements your existing benefits — including EAP, HR resources, and medical or mental-health providers. Chaplains are trained to know their scope and to help employees connect with the right professional when a situation calls for it.",
      ],
    },
    roles: {
      title: "Care Across Every Shift",
      cards: [
        {
          label: "Front-of-House & Counter Teams",
          body:
            "Steady presence for servers, hosts, bartenders, baristas, and quick-service counter staff — the people carrying every guest interaction on their shoulders.",
        },
        {
          label: "Kitchen & Back-of-House",
          body:
            "Non-clinical care for cooks, chefs, dishwashers, prep, and quick-service kitchen crews — support that respects the pace of the line and the toll it takes.",
        },
        {
          label: "Hotel & Guest Services",
          body:
            "Confidential support for front desk, concierge, valet, and guest-experience staff who absorb the emotional weight of the stay.",
        },
        {
          label: "Housekeeping",
          body:
            "Care for the crews doing quiet, physically demanding work — often across languages, schedules, and family responsibilities.",
        },
        {
          label: "Supervisors, Managers & Franchise Operators",
          body:
            "Chaplain support for shift leads, GMs, franchise operators, and department heads carrying both the operation and their team's well-being.",
        },
        {
          label: "Multi-Site & Franchise Leadership",
          body:
            "Coordination and leadership consultation across concepts, brands, franchise groups, and locations — helping care scale with your footprint.",
        },
      ],
    },
  },

  "construction-skilled-trades": {
    slug: "construction-skilled-trades",
    eyebrow: "Industries We Serve",
    h1: "Workplace Chaplain Support for Construction & Skilled Trades",
    lede:
      "Relationship-based care for the people building, repairing, maintaining, and operating the workplaces and communities around us.",
    heroImage: {
      src: "/images/industries/construction-skilled-trades/hero.jpg",
      alt: "Two construction workers in hard hats and safety vests having a natural conversation on an active jobsite.",
    },
    rolesImage: {
      src: "/images/industries/construction-skilled-trades/skilled-trades.jpg",
      alt: "A skilled tradesperson welding steel framing outdoors, representing field crews and skilled trades.",
    },
    meta: {
      title:
        "Workplace Chaplain Support for Construction & Skilled Trades | California Corporate Chaplains",
      description:
        "Confidential, non-clinical workplace chaplain care for construction and skilled-trades crews — field, foremen, project management, office, and leadership. 24/7 on-call chaplain access included.",
    },
    whyItMatters: {
      title: "Why Chaplain Support Matters Here",
      body:
        "Construction and the trades run on relationships. Crews trust each other because they have to, and hard news travels fast on a jobsite. CCCHAPS chaplains meet crews where they work — in the yard, at the trailer, on the walk — as a familiar, confidential person outside the reporting line who's simply there when someone needs to talk.",
      challenges: [
        "Physically demanding work and the cumulative strain it can place on employees and families",
        "Project deadlines, weather delays, and the pressure of the schedule",
        "Travel, out-of-town work, and long stretches away from family",
        "Family separation and the strain it creates for spouses, children, and crews",
        "Financial uncertainty across seasons, layoffs, or between projects",
        "Jobsite pressure — difficult incidents, cross-trade tension, and the emotional weight employees may carry after the workday ends",
        "Grief and loss, including sudden death in a crew or in an employee's family",
        "Injury or serious illness that changes what a family can carry",
        "Workforce transitions, retirements, and helping the next crew step up",
        "Foreman and superintendent responsibility for both production and people",
      ],
    },
    howWeSupport: {
      title: "How CCCHAPS Supports Your Team",
      paragraphs: [
        "A CCCHAPS chaplain visits crews where the work happens. Conversations are voluntary, private, and always on the employee's terms — during a break, at the tailgate meeting, before start-of-shift, or by phone from the truck. Chaplains learn the crews, the projects, and the culture, so support isn't parachuted in when something goes wrong.",
        "Every Care Team Package includes 24/7 on-call chaplain access, CALLACHAP, the CCCHAPS Care Portal, and CCCHAPS On Demand. That means a foreman finishing paperwork at 9 pm, a technician on the road, or a family member back at home can reach a chaplain privately — not just during scheduled site visits. Confidential support extends to employees and their immediate household members.",
        "CCCHAPS complements — it does not replace — safety programs, EAP, HR, medical, mental-health, legal, or emergency services. Chaplains work within their scope and help employees connect to the right professional when a situation calls for one.",
      ],
    },
    roles: {
      title: "Care Across the Whole Jobsite",
      cards: [
        {
          label: "Field Crews",
          body:
            "Presence at tailgate meetings, breaks, and walk-arounds — a familiar, confidential person outside the crew hierarchy.",
        },
        {
          label: "Skilled Trades",
          body:
            "Care for the specialists — electricians, plumbers, welders, operators — who carry expertise, deadlines, and often family strain at once.",
        },
        {
          label: "Foremen & Supervisors",
          body:
            "Support for the people responsible for both production and their people — including difficult conversations, significant incidents, and the responsibility of supporting people while keeping work moving.",
        },
        {
          label: "Project Management",
          body:
            "Confidential care for PMs, superintendents, and estimators navigating schedule pressure and cross-trade responsibility.",
        },
        {
          label: "Office & Operations",
          body:
            "Non-clinical support for accounting, HR, scheduling, and administration — the people making the projects possible off-site.",
        },
        {
          label: "Ownership & Leadership",
          body:
            "Chaplain support and leadership consultation for owners, principals, and executives carrying the long view for the company.",
        },
      ],
    },
  },

  "real-estate-brokerages-title-services": {
    slug: "real-estate-brokerages-title-services",
    eyebrow: "Real Estate, Brokerages & Title Services",
    h1: "Chaplain care for the people behind every transaction.",
    lede:
      "Real estate professionals work in an industry built around people, major life decisions, financial pressure, changing markets, and demanding schedules. CCCHAPS provides confidential, relationship-based chaplain support for real estate agents, brokers, office teams, title professionals, and the people who support them.",
    accessLine:
      "Scheduled on-site, remote, or blended support — with 24/7 on-call chaplain access.",
    heroImage: {
      src: "/images/industries/real-estate-brokerages-title-services/hero.jpg",
      alt: "A confident brokerage team lead standing in a modern real estate office while colleagues collaborate at desks with laptops and computers behind her.",
    },
    rolesImage: {
      src: "/images/industries/real-estate-brokerages-title-services/office-team.jpg",
      alt: "Two business professionals shaking hands across a desk in a modern real estate meeting room with contract paperwork visible.",
    },
    meta: {
      title:
        "Real Estate, Brokerages & Title Services | Workplace Chaplain Support — California Corporate Chaplains",
      description:
        "Confidential, non-clinical workplace chaplain care for real estate agents, brokers, brokerage office staff, title and escrow professionals, and organizational leadership — 24/7 on-call chaplain access included.",
    },
    whyItMatters: {
      title: "Behind every closing is a person carrying real-life pressures.",
      body:
        "Real estate can be rewarding, but it also brings irregular schedules, commission uncertainty, demanding client expectations, difficult transactions, changing market conditions, and pressure to remain available when clients need help. Employees and professionals are also navigating the same grief, caregiving responsibilities, family concerns, health challenges, and personal transitions that affect every workforce.",
      challenges: [
        "Irregular schedules and weekend work",
        "Commission and financial uncertainty",
        "Difficult or delayed transactions",
        "Client and relationship pressures",
        "Changing market conditions",
        "Workplace and leadership stress",
        "Grief, illness, and family concerns",
        "Burnout and emotional fatigue",
      ],
    },
    howWeSupport: {
      title: "Support that fits the way real estate actually works.",
      paragraphs: [
        "Real estate professionals are rarely limited to a traditional 9-to-5 schedule. CCCHAPS support is designed to remain accessible beyond scheduled office visits.",
        "Chaplains visit brokerage offices, title offices, organizational meetings, or other agreed workplace settings on a scheduled cadence, and every care arrangement includes 24/7 on-call chaplain access. Phone and other available remote-support options help agents, brokers, and staff connect when they are away from the office or working nontraditional schedules. Where CALLACHAP is included in the organization's care arrangement, employees can also use its configured support options as an additional pathway to chaplain care.",
        "CCCHAPS complements — it does not replace — EAP, HR, medical or mental-health treatment, legal services, financial advice, real-estate transaction advice, or emergency services. When a situation calls for one of those resources, the chaplain helps the employee connect and, with permission, walks alongside them through it.",
      ],
    },
    roles: {
      title: "Care across the real estate organization.",
      cards: [
        {
          label: "Real Estate Agents",
          body:
            "Confidential support for professionals balancing clients, transactions, unpredictable schedules, and personal responsibilities.",
        },
        {
          label: "Brokers & Managing Brokers",
          body:
            "A confidential sounding board for leaders carrying responsibility for teams, offices, client relationships, and organizational pressures.",
        },
        {
          label: "Brokerage Office & Administrative Staff",
          body:
            "Support for transaction coordinators, administrators, reception teams, marketing staff, operations personnel, and other employees who help keep the business moving.",
        },
        {
          label: "Title & Escrow Professionals",
          body:
            "Relationship-based support for professionals managing detailed, deadline-driven transactions and serving clients through significant financial and life transitions.",
        },
        {
          label: "Leadership & Ownership",
          body:
            "Confidential chaplain support for owners and organizational leaders navigating workforce concerns, difficult decisions, loss, crisis, and change.",
        },
      ],
    },
    whenChaplainHelps: {
      title: "Present through the moments that affect your people.",
      examples: [
        "Death of a colleague, client, or family member",
        "Serious illness or caregiving",
        "Family and relationship difficulties",
        "Workplace conflict or organizational change",
        "Emotional strain after a difficult transaction",
        "Financial or career uncertainty",
        "Leadership pressure",
        "Personal crisis",
        "Major life transitions",
        "Spiritual or meaning-of-life concerns",
      ],
    },
    complements: {
      title: "A human layer of support alongside what you already provide.",
      body:
        "CCCHAPS complements existing HR resources, employee assistance programs, healthcare benefits, and community services. Chaplains provide confidential, non-clinical relational care and can encourage appropriate professional or community resources when needs extend beyond chaplaincy scope.",
    },
    closingCta: {
      title: "Support the people who keep every transaction moving.",
      body:
        "Whether your organization is a brokerage, real estate office, title company, or related real estate service organization, CCCHAPS can help you explore a chaplain care arrangement built around your people, schedules, and workplace realities.",
      buttonLabel: "Request a Consultation",
    },
  },
};

export const INDUSTRY_LINKS = [
  { to: "/industries/automotive-dealerships", label: "Automotive & Dealerships" },
  { to: "/industries/restaurants-hospitality", label: "Restaurants, Fast-Food Franchises & Hospitality" },
  { to: "/industries/construction-skilled-trades", label: "Construction & Skilled Trades" },
  { to: "/industries/real-estate-brokerages-title-services", label: "Real Estate, Brokerages & Title Services" },
];
